import React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import ProgressiveImage from 'react-progressive-image';

export type ImageDimensionsType = [number, number];
export type ImageProps = {
  alt?: string;
  className?: string;
  dimensions?: ImageDimensionsType;
  src: string;
};

const useStyles = ([width, height]) => {
  return makeStyles((theme: Theme) => ({
    image: {
      height: 'auto',
      maxWidth: '100%',
    },

    frame: {
      aspectRatio: `${width}/${height}`,
      maxHeight: height,
      maxWidth: width,

      '& span': {
        aspectRatio: `${width}/${height}`,
        display: 'block',
        maxHeight: height,
        maxWidth: width,
      },
    },

    loadingAnimation: {
      borderRadius: `${theme.shape.borderRadius} !important`,
      display: 'block',
    },
  }))();
};

export function Image(props: ImageProps): JSX.Element {
  const { alt = '', className = '', dimensions = [112, 112], src } = props;
  const classes = useStyles([...dimensions]);

  const ImageLoading = () => (
    <div className={classes.frame}>
      <Skeleton
        height={'100%'}
        width={'100%'}
        className={classes.loadingAnimation}
      />
    </div>
  );

  const imageStyles = clsx(className && className);
  const srcWithConfig = `${src}?fit=crop&ar=${dimensions[1] /
    dimensions[0]}&min-h=${dimensions[1]}&max-h=${dimensions[1]}&w=${
    dimensions[0]
  }`;

  // note: Browsers automatically determine which version to load
  const options = {
    highest: '&q=100&fm=webp',
    high: '&fm=webp',
    low: '&auto=compress&fm=pjpg',
  };

  return (
    <div className={imageStyles}>
      <ProgressiveImage src={srcWithConfig} placeholder="">
        {(src, loading) => {
          return loading ? (
            <ImageLoading />
          ) : (
            <picture>
              <source srcSet={`${src}${options.highest}`} type="image/webp" />
              <source srcSet={`${src}${options.high}`} type="image/webp" />
              <source srcSet={`${src}${options.low}`} />

              <img
                alt={alt}
                className={classes.image}
                height={dimensions[1]}
                src={src}
                srcSet={`${src} 1x, ${src}&dpr=2 2x, ${src}&dpr=3 3x`}
                width={dimensions[0]}
              />
            </picture>
          );
        }}
      </ProgressiveImage>
    </div>
  );
}
